// frontend/src/components/FeatureGate.jsx
import React from "react";
import PropTypes from "prop-types";
import { LockClosedIcon } from "@heroicons/react/20/solid";
import { PLAN_LIMIT_EVENT } from "../api/client";

/**
 * Fire the global plan-limit event so the existing root-level UpgradeModal host
 * (PlanLimitModalHost in App.jsx) shows the upgrade comparison. Reusing that one
 * modal keeps every upsell path — 402 interceptor, lock chips, gates — coherent
 * (PRIVATE-AREA-PLAN PA1-T3 / PA5-T3).
 *
 * @param {string} feature Human-readable feature name shown in the modal.
 */
export function openUpgradeModal(feature) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(PLAN_LIMIT_EVENT, {
      detail: { feature, current: 0, limit: 0, upgradeUrl: "/billing" },
    }),
  );
}

/**
 * LockChip — a tier pill ("Pro" / "Business") signalling a feature is locked.
 * Tone matches the tier it unlocks at.
 */
export function LockChip({ tier, className }) {
  const tone =
    tier === "Business"
      ? "bg-accent-50 text-accent-700 ring-accent-100"
      : "bg-brand-50 text-brand-700 ring-brand-200";
  return (
    <span
      data-testid="lock-chip"
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${tone} ${className}`}
    >
      <LockClosedIcon className="h-3 w-3" aria-hidden="true" />
      {tier}
    </span>
  );
}
LockChip.propTypes = { tier: PropTypes.string, className: PropTypes.string };
LockChip.defaultProps = { tier: "Pro", className: "" };

/**
 * FeatureGate — wraps a sub-plan feature.
 *
 * - `locked === false`: renders children untouched.
 * - `locked === true`, default: renders children but intercepts interaction,
 *   overlaying a LockChip and opening the UpgradeModal on click — the feature
 *   stays *visible* (aspirational) instead of being hidden or a dead grey button.
 * - `locked === true` + `preview`: renders children blurred & inert behind a
 *   centred "Unlock with <tier>" call-to-action (used for chart/export previews,
 *   PRIVATE-AREA-PLAN PA3-T3/T4).
 *
 * @param {boolean} props.locked  Whether the current plan lacks this feature.
 * @param {string}  props.feature Human-readable name for the modal title.
 * @param {string}  [props.tier]  Tier that unlocks it ("Pro" | "Business").
 * @param {boolean} [props.preview] Use the blurred-preview presentation.
 */
export default function FeatureGate({ locked, feature, tier, preview, children }) {
  if (!locked) return children;

  if (preview) {
    return (
      <div className="relative overflow-hidden rounded-xl">
        <div className="pointer-events-none select-none opacity-60 blur-sm" aria-hidden="true">
          {children}
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/40 text-center">
          <LockChip tier={tier} />
          <button
            type="button"
            onClick={() => openUpgradeModal(feature)}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
          >
            Unlock {feature} with {tier}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => openUpgradeModal(feature)}
      className="group relative block w-full cursor-pointer text-left"
      aria-label={`${feature} — upgrade to ${tier} to unlock`}
    >
      <div className="pointer-events-none opacity-70 transition-opacity group-hover:opacity-90">
        {children}
      </div>
      <span className="absolute right-2 top-2">
        <LockChip tier={tier} />
      </span>
    </button>
  );
}

FeatureGate.propTypes = {
  locked: PropTypes.bool.isRequired,
  feature: PropTypes.string.isRequired,
  tier: PropTypes.string,
  preview: PropTypes.bool,
  children: PropTypes.node,
};
FeatureGate.defaultProps = { tier: "Pro", preview: false, children: null };
